import React, { useCallback, useEffect, useRef, useState } from "react";
import { Card } from "primereact/card";
import { Button } from "primereact/button";
import { Divider } from "primereact/divider";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import mermaid from "mermaid";

import rehypeHighlight from "rehype-highlight";
// highlight.js 공식 테마 (github-dark)
import "highlight.js/styles/github-dark.css";

import "primereact/resources/themes/lara-light-indigo/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";

// 1. HTML의 CSS 규칙을 JS style 객체 형태로 반영
const styles = {
    page: {
        background: "#f5f7fa",
        minHeight: "100vh",
        padding: "40px 0",
        color: "#222",
        fontFamily: '"Pretendard", sans-serif',
        margin: 0
    },
    container: {
        maxWidth: "1200px",
        width: "96%",
        margin: "0 auto"
    },
    homeButtonContainer: {
        padding: "0px 20px"
    },
    homeButton: {
        color: "#2563eb",
        fontWeight: "bold",
        paddingLeft: 0,
        marginBottom: "20px",
        background: "none",
        border: "none",
        cursor: "pointer",
        display: "inline-flex",
        alignItems: "center",
        textDecoration: "none",
        fontSize: "16px"
    },
    card: {
        background: "#ffffff",
        color: "#495057",
        borderRadius: "12px",
        padding: "24px", // HTML .p-card 기준 동일 적용
        boxShadow: "0 2px 8px rgba(0,0,0,.08)"
    },
    title: {
        fontSize: "32px",
        margin: "0 0 10px",
        fontWeight: "bold"
    },
    meta: {
        color: "#666",
        fontSize: "14px",
        marginBottom: "20px"
    },
    category: {
        marginLeft: "8px"
    },
    divider: {
        margin: "20px 0",
        borderTop: "1px solid #dee2e6"
    },
    content: {
        lineHeight: "1.7",
        fontSize: "16px"
    },
    pre: {
        background: "#1e1e1e",
        color: "#d4d4d4",
        padding: "16px",
        borderRadius: "8px",
        overflowX: "auto",
        fontSize: "14px",
        lineHeight: "1.6",
        margin: "16px 0"
    },
    code: {
        fontFamily: "Consolas, Monaco, 'Courier New', monospace",
        color: "#d4d4d4"
    },
    inlineCode: {
        backgroundColor: "#f3f4f6",
        color: "#d63384",
        padding: "2px 6px",
        borderRadius: "4px",
        fontFamily: "Consolas, Monaco, 'Courier New', monospace",
        fontSize: "0.95em"
    }
};

// 2. HTML의 <style> 태그 요소들을 동일하게 구성
const contentStyle = `
.content h2 {
    margin-top: 30px;
    border-bottom: 2px solid #eee;
    padding-bottom: 5px;
    font-size: 24px;
}

.content p {
    margin: 12px 0;
}

/* 1. pre 감싸는 태그 패딩 및 여백 설정 */
.content pre {
    background: #1e1e1e !important;
    padding: 14px 18px !important; /* 폰트가 커진 만큼 약간 넓혀서 여백 균형 조정 */
    border-radius: 8px;
    overflow-x: auto;
    margin: 12px 0 !important;
}

/* 2. highlight.js 및 code 태그 폰트 크기 증대 및 기본 패딩 제거 */
.content pre code,
.content pre code.hljs {
    padding: 0 !important; /* highlight.js 기본 패딩 제거 */
    margin: 0 !important;
    background: transparent !important;
    font-family: Consolas, Monaco, 'Courier New', monospace;
    font-size: 15px !important; /* 💡 기존 13.5px -> 15px로 확대 */
    line-height: 1.55; /* 키운 폰트 크기에 맞춰 적절한 줄간격 유지 */
}

.content img {
    max-width: 100%;
    height: auto;
    display: block;
}
`;

// Mermaid 초기화
mermaid.initialize({
    startOnLoad: false,
    theme: "default",
});

// Mermaid 컴포넌트
function Mermaid({ chart }) {
    const ref = useRef(null);

    useEffect(() => {
        const renderChart = async () => {
            if (!ref.current) return;

            try {
                const id = "mermaid-" + Math.random().toString(36).substring(2);
                const { svg } = await mermaid.render(id, chart);
                ref.current.innerHTML = svg;
            } catch (err) {
                console.error(err);
                ref.current.innerHTML = `<pre style="color:red">${err.message}</pre>`;
            }
        };

        renderChart();
    }, [chart]);

    return <div ref={ref} className="mermaid-chart"></div>;
}

const ContentPage = () => {
    const [searchParams] = useSearchParams();
    const postId = searchParams.get("postId");

    const [content, setContent] = useState(null);

    const handleContent = useCallback(async () => {
        const url = `https://ihongss.com/post/contentdetail.json?pid=${postId}`;
        const { data } = await axios.get(url);

        if (data.result === 1) {
            setContent({
                pid: data.title.pid,
                id: data.title.id,
                data: {
                    title: data.title.title,
                    description: data.title.description,
                    pubDate: data.title.pubDate,
                    formattedDate: new Date(data.title.pubDate).toLocaleDateString("ko-KR"),
                    category: data.title.category
                },
                content: data.content.content
            });
        }
    }, [postId]);

    useEffect(() => {
        if (postId) {
            handleContent();
        }
    }, [postId, handleContent]);

    if (!content) {
        return <div style={{ textAlign: "center", padding: "100px" }}>Loading...</div>;
    }

    const markdownContent = content.content || `${content.data?.description || ""}`;

    return (
        <div style={styles.page}>
            <style>{contentStyle}</style>

            <div style={styles.container}>
                <div style={styles.homeButtonContainer}>
                    <Link to="/" style={{ textDecoration: "none" }}>
                        <Button
                            label="홈으로 돌아가기"
                            icon="pi pi-home"
                            className="p-button-text"
                            style={styles.homeButton}
                        />
                    </Link>
                </div>

                <Card style={styles.card}>
                    <h1 style={styles.title}>
                        {content.data.title}
                    </h1>

                    <div style={styles.meta}>
                        <span>📅 {content.data.formattedDate}</span>

                        {content.data.category && (
                            <span style={styles.category}>
                                | 📁 {content.data.category}
                            </span>
                        )}
                    </div>

                    <Divider style={styles.divider} />

                    <div className="content" style={styles.content}>
                        <ReactMarkdown
                            remarkPlugins={[remarkGfm]}
                            rehypePlugins={[rehypeHighlight]}
                            components={{
                                img({ src, alt }) {
                                    return (
                                        <img
                                            src={src}
                                            alt={alt || ""}
                                            style={{
                                                maxWidth: "100%",
                                                height: "auto",
                                                display: "block",
                                            }}
                                        />
                                    );
                                },

                                code({ inline, className, children, ...props }) {
                                    const match = /language-(\w+)/.exec(className || "");
                                    const isMermaid = match?.[1] === "mermaid";

                                    if (!inline && isMermaid) {
                                        return (
                                            <Mermaid
                                                chart={String(children).replace(/\n$/, "")}
                                            />
                                        );
                                    }

                                    // 인라인 코드
                                    if (inline) {
                                        return (
                                            <code style={styles.inlineCode} {...props}>
                                                {children}
                                            </code>
                                        );
                                    }

                                    // 일반 코드 블록
                                    return (
                                        <pre style={styles.pre}>
                                            <code
                                                className={className}
                                                style={styles.code}
                                                {...props}
                                            >
                                                {children}
                                            </code>
                                        </pre>
                                    );
                                }
                            }}
                        >
                            {markdownContent}
                        </ReactMarkdown>
                    </div>
                </Card>
            </div>

            <footer style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
                © 2016 ihongss.com
            </footer>
        </div>
    );
};

export default ContentPage;